import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchAllTestData } from "../service/service";
import {
  setSpecialDependentChecked,
  resetSpecialDependentChecked,
} from "../Redux_Services/slices/specialDependentSlice";
import {
  TEST_SELECTION_MAP,
  HIDDEN_TEST_ID,
  COUPLED_TESTS,
  DEPENDENT_TESTS,
} from "../Utils/TestNames";

const AI_ANALYSIS_CATEGORY = "AI Analysis";
const BODY_ANALYSIS_CATEGORY = "Body Analysis";
const EXCLUSIVE_CATEGORIES = [AI_ANALYSIS_CATEGORY, BODY_ANALYSIS_CATEGORY];
const SPECIAL_DEP_NAME = "Complete Renal Profile";
const SPECIAL_DEP_NAM2 = "Complete Liver Function Tests";
const SPECIAL_DEP_NAME3 = "Thyroid Profile";

const SPECIAL_DEP_NAMES = new Set([
  SPECIAL_DEP_NAME,
  SPECIAL_DEP_NAM2,
  SPECIAL_DEP_NAME3,
]);

const HIDE_PARAMETER_IDS = new Set(["BL9030" /*, "BL5751", ... */]);

const isSpecialDependent = (name) => SPECIAL_DEP_NAMES.has(name);

const isExclusiveCategory = (category) =>
  EXCLUSIVE_CATEGORIES.includes(category);

const filterToExclusiveCategory = (groups = [], category) => {
  if (!isExclusiveCategory(category)) {
    return groups;
  }
  return groups.filter((g) => g.name === category);
};

const hasSelectionInCategory = (groups = [], category) =>
  groups.some(
    (g) =>
      g.name === category && Array.isArray(g.options) && g.options.length > 0
  );

const getCoupledTestForCategory = (category) =>
  COUPLED_TESTS.filter((g) => g.category === category);

const getDependentTestsForCategory = (category) =>
  DEPENDENT_TESTS.filter((g) => g.category === category);

const getDependentKey = (category, name) => `${category}::${name}`;

// ✅ build parent-state but treat "Complete Renal Profile" specially
const buildDependentParentState = (
  groupsSnapshot = [],
  explicitMap = {},
  isRenalChecked,
  isLiverChecked,
  isThyroidChecked
) => {
  const snapshot = {};

  DEPENDENT_TESTS.forEach((dep) => {
    const key = getDependentKey(dep.category, dep.name);

    if (dep.name === SPECIAL_DEP_NAME) {
      snapshot[key] = isRenalChecked;
      return;
    }

    if (dep.name === SPECIAL_DEP_NAM2) {
      snapshot[key] = isLiverChecked;
      return;
    }

    if (dep.name === SPECIAL_DEP_NAME3) {
      snapshot[key] = isThyroidChecked;
      return;
    }

    // default behaviour
    const selectedOptions =
      groupsSnapshot.find((g) => g.name === dep.category)?.options || [];

    snapshot[key] = dep.ids.some((id) => selectedOptions.includes(id));
  });

  return snapshot;
};

const hasParentStateChanged = (prev = {}, next = {}) => {
  const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
  for (const key of keys) {
    if (!!prev[key] !== !!next[key]) {
      return true;
    }
  }
  return false;
};

export default function SelectTests({ selectedGroups, setSelectedGroups }) {
  const dispatch = useDispatch();
  const {
    testData = [],
    testDataLoading,
    testDataError,
  } = useSelector((state) => state.testDataSlice || {});
  const specialDependentState =
    useSelector((state) => state.specialDependentSlice?.checkedMap) || {};

  const [dependentParentChecked, setDependentParentChecked] = useState({});
  // 🆕 Track explicit master clicks for special dependent test(s)
  const [explicitDependentMaster, setExplicitDependentMaster] = useState({});
  const isRenalChecked = specialDependentState[SPECIAL_DEP_NAME] || false;
  const isLiverChecked = specialDependentState[SPECIAL_DEP_NAM2] || false;
  const isThyroidChecked = specialDependentState[SPECIAL_DEP_NAME3] || false;

  useEffect(() => {
    if (testData.length === 0) {
      dispatch(fetchAllTestData());
    }
  }, [dispatch, testData.length]);

  // Always clear special dependent selections on load/refresh of /tests
  useEffect(() => {
    dispatch(resetSpecialDependentChecked());
    setExplicitDependentMaster({});
  }, [dispatch]);

  function groupByParameterCategory(data) {
    const groups = {};
    (data || []).forEach((item) => {
      const prefix = (item.ParameterID || "").slice(0, 2);
      const category = TEST_SELECTION_MAP[prefix] || "Uncategorized";
      if (!groups[category]) groups[category] = [];
      groups[category].push(item);
    });
    return Object.entries(groups).map(([category, fields]) => ({
      category,
      fields,
      prefix: fields[0]?.ParameterID?.slice(0, 2) || "OT",
    }));
  }

  const groupedTests = useMemo(() => {
    const groups = groupByParameterCategory(testData);

    return groups
      .filter((g) => g.category !== "Uncategorized")
      .map((g) => ({
        ...g,
        fields: (g.fields || []).filter(
          (f) => !HIDE_PARAMETER_IDS.has(f.ParameterID)
        ),
      }))
      .filter((g) => g.fields.length > 0);
  }, [testData]);

  const aiSelectionActive = useMemo(
    () => hasSelectionInCategory(selectedGroups, AI_ANALYSIS_CATEGORY),
    [selectedGroups]
  );
  const bodySelectionActive = useMemo(
    () => hasSelectionInCategory(selectedGroups, BODY_ANALYSIS_CATEGORY),
    [selectedGroups]
  );

  const shouldDisableGroup = (groupCategory) => {
    if (
      aiSelectionActive &&
      groupCategory !== AI_ANALYSIS_CATEGORY &&
      !hasSelectionInCategory(selectedGroups, groupCategory)
    ) {
      return true;
    }
    if (
      bodySelectionActive &&
      groupCategory !== BODY_ANALYSIS_CATEGORY &&
      !hasSelectionInCategory(selectedGroups, groupCategory)
    ) {
      return true;
    }
    return false;
  };

  // IDs that belong to coupled bundles (members stay hidden in simple list)
  const COUPLED_MEMBER_IDS = useMemo(
    () => COUPLED_TESTS.flatMap((g) => g.ids),
    []
  );

  // ✅ Only responsible for updating selectedGroups;
  // dependentParentChecked is handled in useEffect below.
  const applySelectionUpdate = (category, updater) => {
    setSelectedGroups((prev) => {
      const updatedGroups =
        typeof updater === "function" ? updater(prev) : updater;
      const filteredGroups = filterToExclusiveCategory(updatedGroups, category);
      return filteredGroups;
    });
  };

  // ✅ Recompute parent checked state when selections or explicit master state change
  useEffect(() => {
    const nextState = buildDependentParentState(
      selectedGroups,
      explicitDependentMaster,
      isRenalChecked,
      isLiverChecked,
      isThyroidChecked
    );

    setDependentParentChecked((prev) =>
      hasParentStateChanged(prev, nextState) ? nextState : prev
    );
  }, [
    selectedGroups,
    explicitDependentMaster,
    isRenalChecked,
    isLiverChecked,
    isThyroidChecked,
  ]);

  // Selection helpers
  const isSelected = (groupPrefix, parameterID) => {
    const group = selectedGroups.find((group) => group.name === groupPrefix);
    return group ? group.options.includes(parameterID) : false;
  };

  const handleOptionChange = (e, groupPrefix, parameterID) => {
    const isChecked = e.target.checked;

    // Cross-category exclusivity (existing logic)
    if (
      isChecked &&
      ((aiSelectionActive && groupPrefix !== AI_ANALYSIS_CATEGORY) ||
        (bodySelectionActive && groupPrefix !== BODY_ANALYSIS_CATEGORY))
    ) {
      return;
    }

    applySelectionUpdate(groupPrefix, (prev) => {
      const idx = prev.findIndex((g) => g.name === groupPrefix);

      // 👉 SPECIAL CASE: AI_ANALYSIS_CATEGORY = single-select
      if (groupPrefix === AI_ANALYSIS_CATEGORY) {
        let options = [];

        if (isChecked) {
          options = [parameterID];
        } else if (idx > -1) {
          options = prev[idx].options.filter((id) => id !== parameterID);
        }

        const withoutThisGroup = prev.filter((g) => g.name !== groupPrefix);

        if (options.length === 0) {
          return withoutThisGroup;
        }

        return [...withoutThisGroup, { name: groupPrefix, options }];
      }

      // 👉 DEFAULT BEHAVIOUR for all other categories
      let nextGroups;
      if (idx > -1) {
        const current = prev[idx].options;
        const next = isChecked
          ? [...new Set([...current, parameterID])]
          : current.filter((id) => id !== parameterID);

        if (next.length === 0) {
          nextGroups = [...prev.slice(0, idx), ...prev.slice(idx + 1)];
        } else {
          nextGroups = [
            ...prev.slice(0, idx),
            { name: groupPrefix, options: next },
            ...prev.slice(idx + 1),
          ];
        }
      } else {
        nextGroups = [...prev, { name: groupPrefix, options: [parameterID] }];
      }

      return nextGroups;
    });
  };

  // MASTER TOGGLE for sets (used by dependent master + coupled master)
  // IMPORTANT: For dependent masters we will pass FULL ids (including hidden)
  const handleMasterToggle = (e, groupPrefix, idsToToggle, parentName) => {
    const isChecked = e.target.checked;

    // Cross-category exclusivity (existing logic)
    if (
      isChecked &&
      ((aiSelectionActive && groupPrefix !== AI_ANALYSIS_CATEGORY) ||
        (bodySelectionActive && groupPrefix !== BODY_ANALYSIS_CATEGORY))
    ) {
      return;
    }

    // 🔹 Track explicit click for special bundles
    if (isSpecialDependent(parentName)) {
      dispatch(
        setSpecialDependentChecked({
          name: parentName,
          checked: isChecked,
        })
      );
    }

    // (keep your existing applySelectionUpdate logic below this)
    applySelectionUpdate(groupPrefix, (prev) => {
      const idx = prev.findIndex((g) => g.name === groupPrefix);

      // 👉 SPECIAL CASE: AI_ANALYSIS_CATEGORY = single-select
      if (groupPrefix === AI_ANALYSIS_CATEGORY) {
        const withoutThisGroup = prev.filter((g) => g.name !== groupPrefix);

        if (!isChecked) {
          // Uncheck -> remove any AI selection
          return withoutThisGroup;
        }

        // Checked -> replace any existing AI options with JUST these ids
        const options = [...idsToToggle]; // all members of this master
        return [...withoutThisGroup, { name: groupPrefix, options }];
      }

      // 👉 DEFAULT BEHAVIOUR for all other categories
      let nextGroups;
      if (idx > -1) {
        const current = prev[idx].options;
        const next = isChecked
          ? [...new Set([...current, ...idsToToggle])]
          : current.filter((id) => !idsToToggle.includes(id));

        if (next.length === 0) {
          nextGroups = [...prev.slice(0, idx), ...prev.slice(idx + 1)];
        } else {
          nextGroups = [
            ...prev.slice(0, idx),
            { name: groupPrefix, options: next },
            ...prev.slice(idx + 1),
          ];
        }
      } else {
        nextGroups = [...prev, { name: groupPrefix, options: idsToToggle }];
      }

      return nextGroups;
    });
  };
  // SELECT ALL: include visible simple items + ALL coupled members + ALL dependent members (including hidden)
  const handleSelectAll = (groupCategory) => {
    applySelectionUpdate(groupCategory, (prev) => {
      const idx = prev.findIndex((g) => g.name === groupCategory);
      const groupFields =
        groupedTests.find((g) => g.category === groupCategory)?.fields || [];

      // SIMPLE visible IDs (hide HIDDEN + hide COUPLED members)
      const simpleVisible = groupFields
        .filter(
          (f) =>
            !HIDDEN_TEST_ID.includes(f.ParameterID) &&
            !COUPLED_MEMBER_IDS.includes(f.ParameterID)
        )
        .map((f) => f.ParameterID);

      // COUPLED members (all)
      const coupledDefs = getCoupledTestForCategory(groupCategory);
      const coupledMembers = coupledDefs.flatMap((g) => g.ids);

      // DEPENDENT members (ALL, including hidden)
      const depDefs = getDependentTestsForCategory(groupCategory);
      const dependentMembersALL = depDefs.flatMap((g) => g.ids);

      const allOptions = Array.from(
        new Set([...simpleVisible, ...coupledMembers, ...dependentMembersALL])
      );

      const isSelectingAll = !(
        idx > -1 && prev[idx].options.length === allOptions.length
      );

      if (
        isSelectingAll &&
        ((hasSelectionInCategory(prev, AI_ANALYSIS_CATEGORY) &&
          groupCategory !== AI_ANALYSIS_CATEGORY) ||
          (hasSelectionInCategory(prev, BODY_ANALYSIS_CATEGORY) &&
            groupCategory !== BODY_ANALYSIS_CATEGORY))
      ) {
        return prev;
      }

      let nextGroups;
      if (idx > -1) {
        const current = prev[idx].options;
        if (current.length === allOptions.length) {
          nextGroups = prev.filter((g) => g.name !== groupCategory);
        } else {
          nextGroups = [
            ...prev.slice(0, idx),
            { name: groupCategory, options: allOptions },
            ...prev.slice(idx + 1),
          ];
        }
      } else {
        nextGroups = [...prev, { name: groupCategory, options: allOptions }];
      }
      return nextGroups;
    });
  };

  // FULL selection check must match what SELECT ALL includes
  const isGroupSelected = (groupCategory) => {
    const group = selectedGroups.find((g) => g.name === groupCategory);
    const groupFields =
      groupedTests.find((g) => g.category === groupCategory)?.fields || [];

    const simpleVisible = groupFields
      .filter(
        (f) =>
          !HIDDEN_TEST_ID.includes(f.ParameterID) &&
          !COUPLED_MEMBER_IDS.includes(f.ParameterID)
      )
      .map((f) => f.ParameterID);

    const coupledDefs = getCoupledTestForCategory(groupCategory);
    const coupledMembers = coupledDefs.flatMap((g) => g.ids);

    const depDefs = getDependentTestsForCategory(groupCategory);
    const dependentMembersALL = depDefs.flatMap((g) => g.ids);

    const allOptions = Array.from(
      new Set([...simpleVisible, ...coupledMembers, ...dependentMembersALL])
    );
    return group ? group.options.length === allOptions.length : false;
  };

  if (testDataLoading) return <div>Loading tests...</div>;
  if (testDataError && (!testData || testData.length === 0))
    return <div>Error loading tests!</div>;

  return (
    <div>
      <ToastContainer />
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto justify-items-center">
        {groupedTests.map((group) => {
          const depDefs = getDependentTestsForCategory(group.category);
          const groupDisabled = shouldDisableGroup(group.category);

          return (
            <div
              key={group.category}
              className="bg-white rounded-3xl shadow-md w-80 h-[28rem] flex flex-col"
            >
              <div className="bg-[#845BB3] rounded-t-3xl py-5 text-center text-white">
                <span className="font-semibold">{group.category}</span>
              </div>

              <div className="mt-4 px-8 flex-1 w-full overflow-y-auto overflow-x-hidden">
                {/* SIMPLE VISIBLE CHECKBOXES */}
                {group.fields
                  .filter(
                    (f) =>
                      !HIDDEN_TEST_ID.includes(f.ParameterID) &&
                      !COUPLED_MEMBER_IDS.includes(f.ParameterID)
                  )
                  .map((f) => (
                    <label
                      key={f.ParameterID}
                      className="flex items-start gap-2 mt-3"
                    >
                      <input
                        type="checkbox"
                        id={f.ParameterID}
                        name={f.name}
                        checked={isSelected(group.category, f.ParameterID)}
                        onChange={(e) =>
                          handleOptionChange(e, group.category, f.ParameterID)
                        }
                        className="h-4 w-4 mt-1 disabled:cursor-not-allowed"
                        disabled={groupDisabled}
                      />
                      <span className="text-[#845BB3] font-medium break-words">
                        {f.name}
                      </span>
                    </label>
                  ))}

                {/* DEPENDENT MASTER CHECKBOXES */}
                {/* DEPENDENT MASTER CHECKBOXES */}
                {depDefs.map((dep) => {
                  const parentKey = getDependentKey(group.category, dep.name);
                  const selectedCount = dep.ids.filter((id) =>
                    isSelected(group.category, id)
                  ).length;

                  // ✅ Use special flags for the two bundles, state for others
                  let masterChecked;
                  if (dep.name === SPECIAL_DEP_NAME) {
                    masterChecked = isRenalChecked;
                  } else if (dep.name === SPECIAL_DEP_NAM2) {
                    masterChecked = isLiverChecked;
                  } else if (dep.name === SPECIAL_DEP_NAME3) {
                    masterChecked = isThyroidChecked;
                  } else {
                    masterChecked = !!dependentParentChecked[parentKey];
                  }

                  const isSpecial = isSpecialDependent(dep.name);

                  // For special bundles, never show indeterminate:
                  const isPartiallySelected =
                    !isSpecial &&
                    masterChecked &&
                    selectedCount < dep.ids.length;

                  return (
                    <label
                      key={dep.name}
                      className="flex items-start gap-2 mt-3"
                    >
                      <input
                        type="checkbox"
                        id={dep.name}
                        name={dep.name}
                        checked={masterChecked}
                        aria-checked={
                          isPartiallySelected ? "mixed" : masterChecked
                        }
                        ref={(el) => {
                          if (!el) return;
                          // Only non-special dependent tests get indeterminate state
                          el.indeterminate = isPartiallySelected;
                        }}
                        onChange={(e) =>
                          handleMasterToggle(
                            e,
                            group.category,
                            dep.ids, // include hidden
                            dep.name
                          )
                        }
                        className="h-4 w-4 mt-1 disabled:cursor-not-allowed"
                        disabled={groupDisabled}
                      />
                      <span className="text-[#845BB3] font-medium break-words">
                        {dep.name}
                      </span>
                    </label>
                  );
                })}

                {/* COUPLED MASTERS (bundles) */}
                {getCoupledTestForCategory(group.category).map((couple) => {
                  const checked = couple.ids.every((id) =>
                    isSelected(group.category, id)
                  );
                  return (
                    <label
                      key={couple.name}
                      className="flex items-start gap-2 mt-3"
                    >
                      <input
                        type="checkbox"
                        id={couple.name}
                        name={couple.name}
                        checked={checked}
                        onChange={(e) =>
                          handleMasterToggle(
                            e,
                            group.category,
                            couple.ids,
                            couple.name
                          )
                        }
                        className="h-4 w-4 mt-1 disabled:cursor-not-allowed"
                        disabled={groupDisabled}
                      />
                      <span className="text-[#845BB3] font-medium break-words">
                        {couple.name}
                      </span>
                    </label>
                  );
                })}
              </div>

              {group.category !== AI_ANALYSIS_CATEGORY && (
                <div className="px-6 py-4">
                  <button
                    onClick={() => handleSelectAll(group.category)}
                    className="bg-[#845BB3] text-white rounded-md px-6 py-2 text-sm font-bold self-end disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={groupDisabled}
                  >
                    {isGroupSelected(group.category)
                      ? "DESELECT ALL"
                      : "SELECT ALL"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
